/**
 * Filtering and sorting for the My Campaigns table. Kept pure so the route
 * only has to render what these return. The customer, deleted, and approval
 * gates stay in the route; they decide which rows reach this.
 */

import type { Campaign, Product } from "./api.ts";
import type { MailFilter } from "./catalog.ts";

export type CampaignListSort = "oldest" | "name" | "name-desc" | "updated" | "addresses";

export interface CampaignListSearch {
  q?: string;
  mail?: MailFilter;
  size?: string;
  pages?: number;
  /** Omitted means newest first, the order the table has always used. */
  sort?: CampaignListSort;
  approve?: boolean;
}

const MAILS = new Set<MailFilter>(["addressed", "unaddressed"]);
const SORTS = new Set<CampaignListSort>(["oldest", "name", "name-desc", "updated", "addresses"]);

/** The label the table shows, so a blank name is searchable and sortable. */
const displayName = (c: Pick<Campaign, "name">): string =>
  c.name === "" ? "Untitled Campaign" : c.name;

/** Drops anything that is not a real facet value, so a bad URL shows everything. */
export function parseCampaignListSearch(raw: Record<string, unknown>): CampaignListSearch {
  const search: CampaignListSearch = {};
  if (typeof raw.q === "string" && raw.q !== "") search.q = raw.q;
  if (typeof raw.mail === "string" && MAILS.has(raw.mail as MailFilter)) {
    search.mail = raw.mail as MailFilter;
  }
  if (typeof raw.size === "string" && raw.size !== "") search.size = raw.size;
  const pages = typeof raw.pages === "number" ? raw.pages : Number(raw.pages);
  if (Number.isInteger(pages) && pages > 0) search.pages = pages;
  if (typeof raw.sort === "string" && SORTS.has(raw.sort as CampaignListSort)) {
    search.sort = raw.sort as CampaignListSort;
  }
  if (raw.approve === true || raw.approve === "true") search.approve = true;
  return search;
}

export const hasActiveCampaignFilters = (s: CampaignListSearch): boolean =>
  Boolean(s.q || s.mail || s.size || s.pages || s.sort);

/** Filters combine with AND. Equal sort keys keep their incoming order. */
export function filterCampaigns(
  campaigns: Campaign[],
  products: Product[],
  search: CampaignListSearch,
): Campaign[] {
  const byId = new Map(products.map((p) => [p.id, p]));
  const term = (search.q ?? "").trim().toLocaleLowerCase();
  const matched = campaigns.filter((c) => {
    if (term && !displayName(c).toLocaleLowerCase().includes(term)) return false;
    if (search.mail && c.mail_type !== search.mail) return false;
    if (search.size || search.pages) {
      const product = c.product_id ? byId.get(c.product_id) : undefined;
      if (search.size && product?.size !== search.size) return false;
      if (search.pages && product?.pages !== search.pages) return false;
    }
    return true;
  });
  const cmp = (a: Campaign, b: Campaign): number => {
    switch (search.sort) {
      case "oldest":
        return a.created_at - b.created_at;
      case "name":
        return displayName(a).localeCompare(displayName(b));
      case "name-desc":
        return displayName(b).localeCompare(displayName(a));
      case "updated":
        return b.updated_at - a.updated_at;
      case "addresses":
        return b.address_count - a.address_count;
      default:
        return b.created_at - a.created_at;
    }
  };
  return [...matched].sort((a, b) => cmp(a, b));
}
